import type { ReactNode } from "react";

import { isElectron } from "../env";
import { cn } from "../lib/utils";
import { COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS } from "../workspaceTitlebar";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "./ui/empty";
import { SidebarInset } from "./ui/sidebar";
import { WorkspaceShellHeader } from "./WorkspaceShellPrimitives";

/**
 * The workspace chrome a thread surface falls back to when it has no thread to draw:
 * the same titlebar the chat view occupies, so the window furniture does not jump, with
 * a centered explanation in place of the conversation. The `timeline` layout instead
 * gives the children the conversation's own column, for a placeholder in its shape.
 */
export function WorkspaceEmptyStateFrame(props: {
  readonly heading: string;
  readonly children: ReactNode;
  readonly layout?: "centered" | "timeline";
}) {
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden bg-background">
      <WorkspaceShellHeader
        className={cn(
          "border-b border-border px-3 transition-[padding-left] duration-200 ease-linear motion-reduce:transition-none sm:px-5",
          isElectron ? "workspace-topbar drag-region" : "workspace-topbar",
          COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS,
        )}
      >
        <span
          className={cn(
            "truncate text-muted-foreground/60",
            isElectron ? "text-xs wco:pr-[var(--workspace-native-controls-inset)]" : "text-sm",
          )}
        >
          {props.heading}
        </span>
      </WorkspaceShellHeader>
      {props.layout === "timeline" ? (
        <div className="min-h-0 flex-1 overflow-hidden px-3 py-6 sm:px-5">{props.children}</div>
      ) : (
        <Empty className="flex-1">
          <div className="w-full max-w-lg px-8 py-12">{props.children}</div>
        </Empty>
      )}
    </div>
  );
}

/**
 * The workspace slot a thread surface occupies when it draws no thread, so an empty
 * state and the chat view it replaces claim the same height and scrolling behavior.
 */
export function WorkspaceEmptyStateInset(props: { readonly children: ReactNode }) {
  return (
    <SidebarInset className="h-dvh min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground">
      {props.children}
    </SidebarInset>
  );
}

/**
 * What every workspace empty state says: an optional icon and title over one line of
 * explanation, with the way out beneath it. Shared so the surfaces that answer for a
 * missing thread stay typographically indistinguishable from one another.
 */
export function WorkspaceEmptyStateMessage(props: {
  readonly icon?: ReactNode;
  readonly title?: string;
  readonly detail: ReactNode;
  readonly action?: ReactNode;
}) {
  return (
    <>
      <EmptyHeader className="max-w-none">
        {props.icon}
        {props.title === undefined ? null : (
          <EmptyTitle className="text-xl text-foreground">{props.title}</EmptyTitle>
        )}
        <EmptyDescription className="mt-2 text-sm text-muted-foreground/78">
          {props.detail}
        </EmptyDescription>
      </EmptyHeader>
      {props.action === undefined ? null : (
        <div className="mt-6 flex justify-center">{props.action}</div>
      )}
    </>
  );
}
